import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import PropTypes from 'prop-types'

import styled from 'styled-components'

import { RestaurantContext } from '../../RestaurantDesignerHandler'
import { useMutation } from 'components/graphql'
import { mutation, defaultDndColor } from '../../helpers'
import { getShape } from './shapesParams'
import { ColorPicker, Form/*, Number*/, Text } from 'components/forms'
import ContentBlock from 'components/ContentBlock'
import Divider from 'components/Divider'
import Button from 'components/Button'

import { StyledShape } from './styled'
import { TabeleTypes, WallTypes, InteriorItemTypes, TABLE_TYPE, WALL_TYPE, INTERIOR_TYPE } from './consts'

const StyledSubmitButton = styled(Button)`
	width: 200px;
	margin-top: 20px;

	&:disabled {
		background: #b5b5b5;
	}
`

const FormBlock = ({ mutations: { create: { rawCreateFurniture }, update: { rawUpdateFurniture } }, startExpanded = false, label, type }) => {
	const shapeTypes = (function () {
		switch (type) {
			case TABLE_TYPE:
				return TabeleTypes
			case WALL_TYPE:
				return WallTypes
			case INTERIOR_TYPE:
				return InteriorItemTypes
			default:
				return []
		}
	}())

	const [t] = useTranslation()
	const { activeFurnitureId, setActiveFurnitureId, currentProjectIds, currentProjectData } = useContext(RestaurantContext)
	const [chosenFurniture, setChosenFurniture] = useState(null)
	const [createFurniture] = useMutation(rawCreateFurniture)
	const [updateFurniture] = useMutation(rawUpdateFurniture)

	const buttonText = (activeFurnitureId && (type === 'table')) ? t('common.buttons.save') : t('common.buttons.add')

	useMemo(()=> {
		if(!activeFurnitureId) {
			setChosenFurniture(null)
		}
	}, [activeFurnitureId])

	useEffect(() => {
		setChosenFurniture(null)
	}, [activeFurnitureId])

	/* const sizeValidation = () => ({
		validate: value => {
			let error = null
			if(value > 500) {
				error = t('restaurant_designer_section.validations.dnd_too_big')
			} else if (value < 40) {
				error = t('restaurant_designer_section.validations.dnd_too_small')
			}
			return error
		}
	}) */

	const getNewData = (oldProp, newProp) => newProp ? newProp : oldProp
	const createFurnitureItem = (fields) => {
		const shapeProps = getShape(chosenFurniture.item)
		const fieldsToSend = {
			...currentProjectIds,
			positionLeft: 100,
			positionTop: 100,
			objectType: 'object',
			objectKind: shapeProps.objectKind,
			width: shapeProps.width,
			height: shapeProps.height,
			...fields
		}

		setActiveFurnitureId(null)
		setChosenFurniture(null)

		return mutation(createFurniture, { fields: [fieldsToSend] })
	}

	const updateFurnitureItem = (fields, itemsClone) => {
		let updatedItem
		itemsClone.find(oldItem => {
			if (oldItem.id === activeFurnitureId) {
				updatedItem = {
					componentColor: getNewData(oldItem.componentColor, fields.componentColor),
					caption: getNewData(oldItem.caption, fields.caption),
					id: oldItem.id
				}
				return true
			}
			return false
		})
		setActiveFurnitureId(null)
		setChosenFurniture(null)

		return mutation(updateFurniture, { fields: [updatedItem] })
	}

	const onSubmit = (fields) => {
		const itemsClone = [...currentProjectData.roomData.furniture]

		if ((activeFurnitureId === null) || (activeFurnitureId && (type === 'interior'))) {
			return createFurnitureItem(fields)
		}
		return updateFurnitureItem(fields, itemsClone)
	}

	const getInit = () => chosenFurniture || { height: 60, width: 60, componentColor: defaultDndColor, caption: '', shape: 'round' }
	if(activeFurnitureId && !chosenFurniture) {
		currentProjectData.roomData.furniture.find(item => {
			if(item.id === activeFurnitureId) {
				setChosenFurniture(item)//do clone?
				return true
			}
			return false
		})
	}

	const renderShapeButtons = (formApi) => {
		return shapeTypes.map((item, key) => {
			const shapeProps = getShape(item)
			const { Component, shape, title } = shapeProps
			const isActive = chosenFurniture ? (chosenFurniture.item === item) : false

			return (
				<StyledShape
					title={title}
					type={'button'}
					key={key}
					className={isActive ? 'active' : ''}
					onClick={()=>onShapeClick(item, shape, formApi)}
				>
					<Component key={key} id={item} shape={shape}/>
				</StyledShape>
			)
		})
	}

	const onShapeClick = (item, shape, formApi) => {
		setChosenFurniture({ item })
		formApi.setValue('shape', shape)
	}

	return (
		<Form
			key={activeFurnitureId}
			initialValues={getInit()}
			onSubmit={onSubmit}
		>
			{({ formApi }) => (
				<ContentBlock style={type === 'table' ? {} : { marginTop: '10px' }} startExpanded={startExpanded} onHeaderClick={()=>setActiveFurnitureId(null)} label={label} blockId={type}>
					<div style={{ display: 'flex', flexDirection: 'column' }}>
						<div>
							<Text field={'id'} hidden/>
						</div>
						{type === 'table' &&
							<>
								{/* <div style={{ display: 'flex' }}>
									<Number
										field='width'
										withControls={true}
										label={t('restaurant_designer_section.fields.item_creator_width')}
										{...sizeValidation()}
										style={{ width: '140px', marginRight: '30px' }}
									/>
									<Number
										field='height'
										withControls={true}
										label={t('restaurant_designer_section.fields.item_creator_height')}
										{...sizeValidation()}
										style={{ width: '140px' }}
									/>
								</div> */}
								<ColorPicker field='componentColor' label={t('restaurant_designer_section.fields.item_creator_color')} style={{ marginBottom: '20px' }}/>
								<Text field='caption' label={t('restaurant_designer_section.fields.item_creator_caption')} style={{ width: '140px' }}/>

								{ !activeFurnitureId && <Divider visible height={4}/> }
							</>
						}
						<Text field='shape' hidden/>
						{ (!activeFurnitureId || (activeFurnitureId && (type === 'interior'))) &&
							<div style={{ display: 'flex', flexWrap: 'wrap' }}>
								{renderShapeButtons(formApi)}
							</div>
						}
						<div>
							<StyledSubmitButton type='submit' status={'green'} text={buttonText} disabled={!chosenFurniture}/>
						</div>
					</div>
				</ContentBlock>
			)}

		</Form>
	)
}

FormBlock.propTypes = {
	startExpanded: PropTypes.bool,
	label: PropTypes.string,
	type: PropTypes.string.isRequired,
	mutations: PropTypes.object.isRequired
}

export default FormBlock
